import { inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { throwError, catchError, map, tap } from 'rxjs';
import { TokenStorageService } from '../../shared/services/token-storage.service';
import { environment } from '../../../environments/environment';
import { LoadingStatus } from '../../shared/enums/loading';
import { toAuthTokens } from '../auth.adapters';
import { StorageService } from '@anihis/shared/src/services/storage.service';
import { OAuthClientId } from '@anihis/shared/src/enum/oauth-client-id';
import { StorageKeyConstants } from '@anihis/shared/src/constants/storage-key-constants';
import { TabsStateService } from '../../shared/services/tabs-state.service';
import { translateServerMessage } from '../../shared/functions/extract-server-error';
import { SignalRService } from '../../shared/services/signalr.service';

type LoginState = {
  status: LoadingStatus;
  error: string | null;
};

const initialState: LoginState = {
  status: LoadingStatus.Loaded,
  error: null,
};

export const LoginStore = signalStore(
  withState(initialState),
  withMethods(
    (
      store,
      http = inject(HttpClient),
      storage = inject(TokenStorageService),
      router = inject(Router),
      storageService = inject(StorageService),
      tabsState = inject(TabsStateService),
      signalRService = inject(SignalRService),
    ) => ({
      login(form: { email: string; password: string }, returnUrl?: string) {
        patchState(store, { status: LoadingStatus.Loading, error: null });

        // Read language from storage (async), then persist and perform login
        storageService
          .getItem<string | null>(StorageKeyConstants.LANGUAGE_KEY)
          .then((lang) => {
            if (!lang && typeof localStorage !== 'undefined') {
              lang = localStorage.getItem('lang') ?? null;
            }
            if (lang) {
              storageService
                .setItem(StorageKeyConstants.LANGUAGE_KEY, lang)
                .catch(() => {});
            }
          })
          .catch(() => {});

        // Clear token storage (synchronous)
        storage.clear();

        const body = new HttpParams()
          .set('grant_type', 'password')
          .set('client_id', environment.oauth?.clientId || OAuthClientId.Portal)
          .set('username', form.email)
          .set('password', form.password)
          .set(
            'scope',
            environment.oauth?.scope ||
              'openid profile email offline_access api',
          );

        const headers = new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json',
        });

        return http
          .post<any>(`${environment.apiUrl}/Token`, body.toString(), {
            headers,
          })
          .pipe(
            map((res) => toAuthTokens(res)),
            tap((tokens) => storage.save(tokens)),
            tap(() => signalRService.connectForStaffRole()),
            tap(() => {
              if (typeof tabsState?.sanitizeForCurrentRole === 'function') {
                tabsState.sanitizeForCurrentRole();
              }
            }),
            tap(() => router.navigateByUrl(returnUrl || '/')),
            tap(() => patchState(store, { status: LoadingStatus.Loaded })),
            catchError((error: HttpErrorResponse) => {
              // Log full error to console for debugging
              // eslint-disable-next-line no-console
              console.error('Login error response:', error);

              // Prefer server-provided OAuth/ProblemDetails message so users see
              // actionable errors (e.g. "You must claim your account before logging in.").
              const serverMsg =
                error.error?.error_description ||
                error.error?.detail ||
                error.error?.title;

              const translatedServerMsg = serverMsg
                ? translateServerMessage(serverMsg)
                : null;

              const msg =
                translatedServerMsg ||
                (error.status === 400 || error.status === 401
                  ? 'Invalid email or password.'
                  : error.message || 'Login failed');

              patchState(store, {
                status: LoadingStatus.Loaded,
                error: msg,
              });
              return throwError(() => new Error(msg));
            }),
          );
      },

      resetError() {
        patchState(store, { error: null });
      },
    }),
  ),
);
